import type { BundledLanguage } from "shiki";
import { bundledLanguages, codeToTokens } from "shiki";
import type {
  CodeHighlighterPlugin,
  HighlightOptions,
  ThemeInput,
} from "streamdown";

type HighlightResult = NonNullable<
  ReturnType<CodeHighlighterPlugin["highlight"]>
>;
type HighlightToken = HighlightResult["tokens"][number][number];

export const MARKDOWN_THEMES: [ThemeInput, ThemeInput] = [
  "one-light",
  "one-dark-pro",
];

const CACHE_LIMIT = 200;
const cache = new Map<string, HighlightResult>();

const isSupported = (language: BundledLanguage) => language in bundledLanguages;

const themeKey = (themes: [ThemeInput, ThemeInput]) =>
  themes
    .map((theme) => (typeof theme === "string" ? theme : theme.name))
    .join();

// Streamdown holds on to the last result we handed it, so a call that produces
// nothing freezes the block at whatever it was showing. Mid-stream that means a
// half-written fence stays half-written until the message is re-fetched.
const unhighlighted = (code: string): HighlightResult => ({
  bg: "transparent",
  fg: "inherit",
  tokens: code.split("\n").map((line) => [
    {
      content: line,
      color: "inherit",
      bgColor: "transparent",
      htmlStyle: {},
      offset: 0,
    } satisfies HighlightToken,
  ]),
});

const remember = (key: string, result: HighlightResult) => {
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
  cache.set(key, result);
};

const highlight = (
  { code, language, themes }: HighlightOptions,
  callback?: (result: HighlightResult) => void
): HighlightResult | null => {
  if (!isSupported(language)) return unhighlighted(code);

  const key = `${themeKey(themes)} ${language} ${code}`;
  const cached = cache.get(key);
  if (cached) return cached;
  if (!callback) return unhighlighted(code);

  codeToTokens(code, {
    lang: language,
    themes: { light: themes[0], dark: themes[1] },
  })
    .then(({ tokens, rootStyle }) => {
      const result: HighlightResult = {
        bg: "transparent",
        fg: "inherit",
        rootStyle,
        tokens,
      };
      remember(key, result);
      callback(result);
    })
    .catch(() => callback(unhighlighted(code)));

  return null;
};

export const shikiHighlighter: CodeHighlighterPlugin = {
  name: "shiki",
  type: "code-highlighter",
  getSupportedLanguages: () =>
    Object.keys(bundledLanguages) as BundledLanguage[],
  getThemes: () => MARKDOWN_THEMES,
  supportsLanguage: isSupported,
  highlight,
};
